import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import {
  Link2, Shield, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Loader2, Globe, Key,
  FileText, CreditCard, Building, Send, Upload, X
} from 'lucide-react';
import { toast } from 'sonner';
import AdoptionJourney from '../../components/shared/AdoptionJourney';

const CLEARINGHOUSES = [
  { id: 'change', name: 'Change Healthcare', desc: 'Largest US clearinghouse network', popular: true },
  { id: 'availity', name: 'Availity', desc: 'Multi-payer portal & clearinghouse', popular: true },
  { id: 'trizetto', name: 'TriZetto / Cognizant', desc: 'Claims routing & analytics', popular: true },
  { id: 'waystar', name: 'Waystar', desc: 'RCM & claims management', popular: false },
  { id: 'office_ally', name: 'Office Ally', desc: 'Free claims submission', popular: false },
];

const PM_SYSTEMS = [
  { id: 'enable', name: 'Enable Healthcare', desc: 'Native integration — full RCM sync', native: true },
  { id: 'athena', name: 'athenaCollector', desc: 'Cloud-based PM/RCM' },
  { id: 'kareo', name: 'Kareo / Tebra', desc: 'PM + billing' },
  { id: 'advancedmd', name: 'AdvancedMD', desc: 'PM + EHR suite' },
  { id: 'practice_fusion', name: 'Practice Fusion', desc: 'Free EHR + billing' },
];

const RevQIntegrations = () => {
  const [path, setPath] = useState(null);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);

  const handleConnect = () => {
    setConnecting(true);
    setTimeout(() => { setConnecting(false); setStep(3); }, 2000);
  };

  const handleAuthorize = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false); setConnected(true); setStep(4);
      toast.success('Integration Connected', { description: `Connected to ${selected?.name}` });
    }, 2000);
  };

  const reset = () => { setPath(null); setStep(1); setSelected(null); setConnecting(false); setConnected(false); };

  if (!path) {
    return (
      <div className="space-y-8" data-testid="revq-integrations-page">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Integrations</h1>
          <p className="text-slate-500 mt-1">Connect clearinghouses, PM systems, and billing tools</p>
        </div>

        <AdoptionJourney app="revq" />

        <Card className="border-violet-200 bg-gradient-to-r from-violet-50/50 to-white" data-testid="enable-native-cta">
          <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-violet-100 flex items-center justify-center"><Sparkles className="h-7 w-7 text-violet-600" /></div>
              <div>
                <div className="flex items-center gap-2"><p className="font-bold text-lg text-slate-900">Enable Healthcare</p><Badge className="bg-violet-100 text-violet-700 text-xs">Native</Badge></div>
                <p className="text-sm text-muted-foreground">Full native billing integration — claims submission, ERA posting, eligibility, and payer rules sync automatically.</p>
              </div>
            </div>
            <Button className="bg-black hover:bg-gray-800 gap-2 shrink-0" onClick={() => { setPath('pm'); setSelected(PM_SYSTEMS[0]); }}><Link2 className="h-4 w-4" /> Connect Enable</Button>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group" onClick={() => setPath('clearinghouse')} data-testid="clearinghouse-path">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto group-hover:bg-emerald-100 transition-colors"><Send className="h-8 w-8 text-emerald-600" /></div>
              <div><h3 className="text-lg font-bold">Clearinghouse</h3><p className="text-sm text-muted-foreground mt-1">Connect a clearinghouse for electronic claims submission and ERA retrieval</p></div>
              <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-xs">Recommended</Badge>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group" onClick={() => setPath('pm')} data-testid="pm-path">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto group-hover:bg-blue-100 transition-colors"><Building className="h-8 w-8 text-blue-600" /></div>
              <div><h3 className="text-lg font-bold">PM System</h3><p className="text-sm text-muted-foreground mt-1">Sync your Practice Management system for charges, demographics, and scheduling</p></div>
              <Badge variant="outline" className="text-xs">Popular</Badge>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group" onClick={() => setPath('payer')} data-testid="payer-path">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto group-hover:bg-amber-100 transition-colors"><Shield className="h-8 w-8 text-amber-600" /></div>
              <div><h3 className="text-lg font-bold">Payer Portals</h3><p className="text-sm text-muted-foreground mt-1">Direct payer connections for eligibility, claims status, and prior auth</p></div>
              <Badge variant="outline" className="text-xs">Coming Soon</Badge>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-sm font-semibold">What gets synced with revQ</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: FileText, label: 'Claims', desc: 'Submit, track, and manage claims electronically' },
                { icon: CreditCard, label: 'ERA/EOB', desc: 'Automated payment posting from remittance' },
                { icon: Shield, label: 'Eligibility', desc: 'Real-time insurance verification' },
                { icon: Send, label: 'Billing Workflows', desc: 'Encounter → claim → payment pipeline' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100">
                  <div className="h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0"><item.icon className="h-4 w-4 text-slate-500" /></div>
                  <div><p className="text-sm font-medium">{item.label}</p><p className="text-xs text-muted-foreground">{item.desc}</p></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (path === 'payer') {
    return (
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="payer-vendors">
        <Button variant="ghost" className="gap-2" onClick={reset}><ArrowLeft className="h-4 w-4" /> Back</Button>
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Payer Portals</h1>
          <p className="text-muted-foreground">Direct connections to major payer systems</p>
        </div>
        {['Blue Cross Blue Shield', 'Aetna', 'UnitedHealthcare', 'Cigna', 'Medicare / CMS'].map(name => (
          <Card key={name} className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><Shield className="h-5 w-5 text-slate-500" /></div>
              <div><p className="font-medium text-sm">{name}</p><p className="text-xs text-muted-foreground">Eligibility, claims status, prior auth</p></div>
            </div>
            <Badge variant="outline" className="text-xs text-slate-400">Coming Soon</Badge>
          </CardContent></Card>
        ))}
      </div>
    );
  }

  const vendors = path === 'clearinghouse' ? CLEARINGHOUSES : PM_SYSTEMS;
  const title = path === 'clearinghouse' ? 'Clearinghouse' : 'PM System';

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="integration-wizard">
      <Button variant="ghost" className="gap-2" onClick={reset}><ArrowLeft className="h-4 w-4" /> Back</Button>
      <div className="flex items-center justify-center gap-2 mb-4">
        {[1, 2, 3, 4].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={cn("h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors", step >= s ? "bg-black text-white" : "bg-slate-200 text-slate-500")}>
              {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
            </div>
            {i < 3 && <div className={cn("w-8 h-px", step > s ? "bg-black" : "bg-slate-200")} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <Card><CardHeader className="text-center"><CardTitle className="text-xl">Select {title}</CardTitle></CardHeader><CardContent className="space-y-3">
          {vendors.map(v => (
            <div key={v.id} className={cn("flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all hover:border-slate-400", selected?.id === v.id && "border-black bg-slate-50", v.native && "border-violet-200 bg-violet-50/30")} onClick={() => setSelected(v)}>
              <div className="flex items-center gap-3">
                <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", v.native ? "bg-violet-100" : "bg-slate-100")}><Globe className="h-5 w-5 text-slate-500" /></div>
                <div><p className="font-medium text-sm">{v.name}</p><p className="text-xs text-muted-foreground">{v.desc}</p></div>
              </div>
              <div className="flex items-center gap-2">
                {v.native && <Badge className="bg-violet-100 text-violet-700 text-xs">Native</Badge>}
                {v.popular && <Badge variant="outline" className="text-xs">Popular</Badge>}
                {selected?.id === v.id && <CheckCircle2 className="h-5 w-5 text-violet-600" />}
              </div>
            </div>
          ))}
          <Button className="w-full bg-black hover:bg-gray-800 mt-4" disabled={!selected} onClick={() => setStep(2)}>Continue with {selected?.name || '...'}</Button>
        </CardContent></Card>
      )}

      {step === 2 && (
        <Card><CardHeader className="text-center"><CardTitle className="text-xl">Connect to {selected?.name}</CardTitle></CardHeader><CardContent className="space-y-4">
          {selected?.native ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-center"><Sparkles className="h-6 w-6 text-violet-600 mx-auto mb-2" /><p className="text-sm font-medium text-violet-800">Enable Healthcare native</p><p className="text-xs text-violet-700 mt-1">Direct API access for claims, ERA, eligibility, and billing sync.</p></div>
              <div className="space-y-2"><Label>Practice ID</Label><Input placeholder="Enter your Enable Practice ID" /></div>
              <div className="space-y-2"><Label>API Key</Label><Input type="password" placeholder="Enter your API key" /></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2"><Label>{title} API URL</Label><Input placeholder="https://api.example.com" /></div>
              <div className="space-y-2"><Label>Client ID / Submitter ID</Label><Input placeholder="Your credentials" /></div>
              <div className="space-y-2"><Label>Client Secret</Label><Input type="password" placeholder="Your secret key" /></div>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Shield className="h-4 w-4 text-violet-600" /><span>All credentials are encrypted. HIPAA compliant.</span></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={handleConnect} disabled={connecting} className="flex-1 bg-black hover:bg-gray-800 gap-2">{connecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting...</> : <>Connect <ArrowRight className="h-4 w-4" /></>}</Button>
          </div>
        </CardContent></Card>
      )}

      {step === 3 && (
        <Card><CardHeader className="text-center"><CardTitle className="text-xl">Authorize Access</CardTitle></CardHeader><CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">revQ needs the following permissions:</p>
          {['Submit Claims Electronically', 'Retrieve ERA/EOB Files', 'Check Eligibility & Benefits', 'Receive Claim Status Updates', 'Post Payments & Adjustments'].map(perm => (
            <div key={perm} className="flex items-start gap-3 p-3 rounded-lg border"><CheckCircle2 className="h-5 w-5 text-violet-600 mt-0.5" /><p className="text-sm font-medium">{perm}</p></div>
          ))}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={handleAuthorize} disabled={connecting} className="flex-1 bg-black hover:bg-gray-800 gap-2">{connecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Authorizing...</> : <><CheckCircle2 className="h-4 w-4" /> Authorize & Sync</>}</Button>
          </div>
        </CardContent></Card>
      )}

      {step === 4 && (
        <Card className="text-center" data-testid="integration-success"><CardContent className="p-8 space-y-4">
          <div className="h-16 w-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8 text-violet-600" /></div>
          <div><h2 className="text-xl font-bold">Connected to {selected?.name}</h2><p className="text-sm text-muted-foreground mt-1">Your {title.toLowerCase()} is now synced with revQ</p></div>
          <div className="rounded-lg border bg-slate-50 p-4 text-left text-sm space-y-1 max-w-sm mx-auto">
            <div className="flex justify-between"><span className="text-muted-foreground">Claims Submission</span><span className="font-medium text-violet-600">Enabled</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">ERA Retrieval</span><span className="font-medium text-violet-600">Enabled</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Eligibility Check</span><span className="font-medium text-violet-600">Enabled</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Payment Posting</span><span className="font-medium text-violet-600">Enabled</span></div>
          </div>
          <Button onClick={reset} className="bg-black hover:bg-gray-800 gap-2 mt-2"><ArrowRight className="h-4 w-4" /> Go to Dashboard</Button>
        </CardContent></Card>
      )}
    </div>
  );
};

export default RevQIntegrations;
